import { inject, injectable } from "inversify";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import type { ISendMessage } from "../sendMessage";
import { simulatePaste } from "@whatslly/commons/simulatePaste";
import { delay, retry } from "@whatslly/commons/utils";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { TEMPLATE_DATA_TRANSFER_TYPE } from "@whatslly/instant-messaging/commons/IImTemplatesStore";

const ITERATIONS_FOR_FIVE_SECONDS = 50;
const waitForWhatsappDragHereToRender = async () => await delay(100);

@injectable()
export class SendMessageService implements ISendMessage {
  private selectors?: WhatsappSelectorsType;

  constructor(@inject(FrontLogger) private logger: FrontLogger) {}

  init(selectors: WhatsappSelectorsType) {
    this.selectors = selectors;
  }

  private async dropFilesToWhatsapp(dataTransfer: DataTransfer) {
    if (!this.selectors) return;
    await waitForWhatsappDragHereToRender();
    const dragFilesElement = document.querySelector(this.selectors.css.dragFilesElement);
    if (!dragFilesElement) return;

    const dropFilesEvent = new DragEvent("drop", {
      dataTransfer,
      bubbles: true,
    });
    dragFilesElement.dispatchEvent(dropFilesEvent);
  }

  private async waitToPasteTextInMiddleDrawer(text: string) {
    await retry(
      () => {
        if (!this.selectors) return;
        const statusElement = document.querySelector(this.selectors.css.pasteFilesStatus);
        const whatsappTextInput = [...document.querySelectorAll(this.selectors.css.middleDrawerCopyableText)].find((e) => {
          const { x, y, width, height } = e.getBoundingClientRect();
          return document.elementFromPoint(x + width / 2, y + height / 2) === e;
        });
        if (!statusElement && whatsappTextInput) return simulatePaste(whatsappTextInput, text);
        throw new Error("Elements not ready");
      },
      { maxRetry: ITERATIONS_FOR_FIVE_SECONDS, delayBetweenRetryMs: 100 }
    );
  }

  private async dropFiles(text: string, files: File[], templateId?: string) {
    if (!this.selectors) return;
    const whatsappPanelBody = document.querySelector(this.selectors.css.whatsappPanelBody);
    if (!whatsappPanelBody) return;

    const dataTransfer = new DataTransfer();
    if (templateId) dataTransfer.setData(TEMPLATE_DATA_TRANSFER_TYPE, templateId);
    files?.forEach((file) => dataTransfer.items.add(file));
    const triggerMiddleDrawerEvent = new DragEvent("dragenter", {
      dataTransfer,
      bubbles: true,
    });

    await this.waitToPasteTextInMiddleDrawer(text);
    // No matter what, don't remove this delay
    await delay(350);

    whatsappPanelBody.dispatchEvent(triggerMiddleDrawerEvent);
    await this.dropFilesToWhatsapp(dataTransfer);
  }

  async sendMessage(message: string, files?: File[], templateId?: string) {
    if (!this.selectors) {
      return;
    }
    const whatsappTextRegularInput = document.querySelector(this.selectors.css.copyableText);
    if (!whatsappTextRegularInput) return;
    if (files && files.length > 0) {
      const hasNonImages = files.some((file) => !file.type.includes("image"));
      if (hasNonImages)
        return this.dropFiles(message, files, templateId).catch((err) => this.logger.error({ err }, "failed to paste files in whatsapp"));

      const dataTransfer = new DataTransfer();
      if (templateId) dataTransfer.setData(TEMPLATE_DATA_TRANSFER_TYPE, templateId);
      files?.forEach((file) => dataTransfer.items.add(file));
      const event = new ClipboardEvent("paste", {
        clipboardData: dataTransfer,
        bubbles: true,
      });
      simulatePaste(whatsappTextRegularInput, message);
      setTimeout(() => whatsappTextRegularInput.dispatchEvent(event), 200);
      return;
    }

    simulatePaste(whatsappTextRegularInput, message);
  }
}
